import { useEffect, Suspense, lazy } from 'react'
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom'
import { QueryClientProvider } from '@tanstack/react-query'
import { Toaster, toast } from 'react-hot-toast'
import { queryClient } from './lib/queryClient'
import { useAuthStore } from './stores/auth'
import { hubService } from './services/hub';
import { ErrorBoundary } from './components/ErrorBoundary'
import { Header } from './components/layout/Header'
import { LoginForm } from './components/forms/LoginForm'
import { RegisterForm } from './components/forms/RegisterForm'

// Lazy load heavy components for better performance
const Dashboard = lazy(() => import('./pages/Dashboard').then(m => ({ default: m.Dashboard })));
const Messages = lazy(() => import('./pages/Messages').then(m => ({ default: m.Messages })));
const HubManagement = lazy(() => import('./pages/HubManagement').then(m => ({ default: m.HubManagement })));
const Analytics = lazy(() => import('./pages/Analytics').then(m => ({ default: m.Analytics })));
const Settings = lazy(() => import('./pages/Settings').then(m => ({ default: m.Settings })));
const HubPage = lazy(() => import('./pages/Hub'));
const AcceptInvitation = lazy(() => import('./pages/AcceptInvitation'));
const Notifications = lazy(() => import('./pages/Notifications'));

// Loading component for Suspense
const PageLoader = () => (
  <div className="flex-1 flex items-center justify-center min-h-[400px]">
    <div className="flex flex-col items-center gap-3">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      <p className="text-sm text-gray-500">Loading...</p>
    </div>
  </div>
);

// Root redirect component
function RootRedirect() {
  const { isAuthenticated, isLoading } = useAuthStore()
  const navigate = useNavigate()

  useEffect(() => {
    if (!isLoading) {
      if (isAuthenticated) {
        navigate('/dashboard', { replace: true })
      } else {
        navigate('/login', { replace: true })
      }
    }
  }, [isAuthenticated, isLoading, navigate])

  // Show loading spinner while determining redirect
  return (
    <div className="flex min-h-screen bg-gray-50 items-center justify-center w-full">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
    </div>
  )
}

// Protected Route Component
function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAuthStore()

  // Show loading spinner while checking authentication
  if (isLoading) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
      </div>
    )
  }

  // If not authenticated, redirect to login
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />
  }

  return <>{children}</>
}

function HubRedirectGuard({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, user } = useAuthStore();
  const navigate = useNavigate();
  const location = useLocation();
  
  useEffect(() => {
    if (!isAuthenticated || !user) return;
    
    // Only run on dashboard or root paths to avoid interfering with other routes
    if (!['/dashboard', '/'].includes(location.pathname)) return;
    
    (async () => {
      try {
        const hubs = await hubService.getHubs();
        if (!hubs || hubs.length === 0) {
          // User has no hubs - create a default hub for them
          try {
            const defaultHub = await hubService.createHub({
              name: `${user.name}'s Hub`,
              description: `Welcome to ${user.name}'s communication hub!`,
              settings: { privacyLevel: 'private' }
            });
            
            // Show success message
            toast.success(`Welcome back! Your hub "${defaultHub.name}" has been created.`);
            
            // Instead of reloading the page, just navigate to dashboard
            // The dashboard will re-fetch hubs and show the new hub
            navigate('/dashboard', { replace: true });
          } catch (hubError: any) {
            console.warn('Failed to create default hub for existing user:', hubError);
            // If hub creation fails, redirect to hub management page
            navigate('/hub', { replace: true });
          }
        }
      } catch (e) {
        console.warn('Failed to check user hubs:', e);
        // If we can't check hubs, let them continue to dashboard
      }
    })();
  }, [isAuthenticated, user, navigate, location.pathname]);
  
  return <>{children}</>;
}

// Layout Component
function Layout({ children }: { children: React.ReactNode }) {
  const { isAuthenticated } = useAuthStore()

  return (
    <div className="h-full min-h-screen bg-gray-50 flex flex-col">
      {isAuthenticated && <Header />}
      <main className="flex-1 flex flex-row min-h-0 h-full">{children}</main>
      <Toaster
        position="top-right"
        toastOptions={{
          duration: 4000,
          style: {
            background: '#363636',
            color: '#fff',
          },
          success: {
            duration: 3000,
            iconTheme: {
              primary: '#10B981',
              secondary: '#fff',
            },
          },
          error: {
            duration: 5000,
            iconTheme: {
              primary: '#EF4444',
              secondary: '#fff',
            },
          },
        }}
      />
    </div>
  )
}

function App() {
  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <Router
          future={{
            v7_startTransition: true,
            v7_relativeSplatPath: true,
          }}
        >
          <Layout>
            <Suspense fallback={<PageLoader />}>
              <Routes>
                {/* Public Routes */}
                <Route path="/" element={<RootRedirect />} />
                <Route path="/login" element={<LoginForm />} />
                <Route path="/register" element={<RegisterForm />} />

                {/* Protected Routes with Lazy Loading */}
                <Route
                  path="/dashboard"
                  element={
                    <ProtectedRoute>
                      <HubRedirectGuard>
                        <Suspense fallback={<PageLoader />}>
                          <Dashboard />
                        </Suspense>
                      </HubRedirectGuard>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/messages"
                  element={
                    <ProtectedRoute>
                      <Suspense fallback={<PageLoader />}>
                        <Messages />
                      </Suspense>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/hub"
                  element={
                    <ProtectedRoute>
                      <Suspense fallback={<PageLoader />}>
                        <HubManagement />
                      </Suspense>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/analytics"
                  element={
                    <ProtectedRoute>
                      <Suspense fallback={<PageLoader />}>
                        <Analytics />
                      </Suspense>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/settings"
                  element={
                    <ProtectedRoute>
                      <Suspense fallback={<PageLoader />}>
                        <Settings />
                      </Suspense>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/notifications"
                  element={
                    <ProtectedRoute>
                      <Suspense fallback={<PageLoader />}>
                        <Notifications />
                      </Suspense>
                    </ProtectedRoute>
                  }
                />
                <Route 
                  path="/hub/:hubId" 
                  element={
                    <ProtectedRoute>
                      <Suspense fallback={<PageLoader />}>
                        <HubPage />
                      </Suspense>
                    </ProtectedRoute>
                  } 
                />
                <Route 
                  path="/accept-invitation/:invitationId" 
                  element={
                    <Suspense fallback={<PageLoader />}>
                      <AcceptInvitation />
                    </Suspense>
                  } 
                />
                <Route 
                  path="/create-hub" 
                  element={
                    <ProtectedRoute>
                      <Suspense fallback={<PageLoader />}>
                        <HubManagement />
                      </Suspense>
                    </ProtectedRoute>
                  } 
                />

                {/* Catch all route */}
                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
            </Suspense>
          </Layout>
        </Router>
      </QueryClientProvider>
    </ErrorBoundary>
  )
}

export default App
